import { useState } from "preact/hooks";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Loader2, X } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  PILL_FILLED_CLASS,
  PILL_OUTLINE_CLASS,
} from "./CancelSubscriptionDialog";
import { getDelayPresets } from "./pause-dates";
import { formatSubscriptionDateForDisplay } from "./formatters";

export interface DelayDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  nextBillingDate?: string | null;
  lastChargeDate?: string | null;
  onConfirm: (date: string, weeks: number) => void;
  confirmPending: boolean;
  /**
   * When true (e.g. opened from the cancel/retention flow), the action is the
   * filled primary button and "Never mind" is the outline secondary. Default
   * keeps "Never mind" primary (proactive use from the manage sheet).
   */
  actionIsPrimary?: boolean;
}

export function DelayDialog({
  open,
  onOpenChange,
  nextBillingDate,
  lastChargeDate,
  onConfirm,
  confirmPending,
  actionIsPrimary = false,
}: DelayDialogProps) {
  const actionVariant = actionIsPrimary ? "default" : "outline";
  const actionPillClass = actionIsPrimary
    ? PILL_FILLED_CLASS
    : PILL_OUTLINE_CLASS;
  const dismissVariant = actionIsPrimary ? "outline" : "default";
  const dismissPillClass = actionIsPrimary
    ? PILL_OUTLINE_CLASS
    : PILL_FILLED_CLASS;
  const presets = getDelayPresets(nextBillingDate, lastChargeDate);
  const [selectedWeeks, setSelectedWeeks] = useState<number | null>(
    open ? (presets[0]?.weeks ?? null) : null,
  );
  const [prevOpen, setPrevOpen] = useState(open);
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) setSelectedWeeks(presets[0]?.weeks ?? null);
  }
  const selectedPreset = presets.find((p) => p.weeks === selectedWeeks) ?? null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        onOpenChange={onOpenChange}
        overlayClassName="tw:z-80"
        className="tw:z-81 tw:p-4"
      >
        <DialogHeader>
          <div className="tw:flex tw:flex-row tw:items-center tw:justify-between tw:gap-2 tw:w-full">
            <DialogTitle
              className="tw:min-w-0 tw:flex-1 tw:pr-1 tw:text-left"
              style={{ color: "var(--so-text, #523D33)" }}
            >
              Delay your delivery
            </DialogTitle>
            <DialogClose
              data-slot="dialog-close"
              render={
                <Button
                  variant="ghost"
                  className="tw:p-0 tw:m-0 tw:shrink-0"
                  size="icon-sm"
                  aria-label="Close"
                />
              }
            >
              <X />
            </DialogClose>
          </div>
          <p className="tw:m-0 tw:text-xs tw:leading-relaxed tw:text-[#523D33]/80">
            Pick how much extra time you need.
          </p>
        </DialogHeader>

        {presets.length > 0 ? (
          <fieldset className="tw:flex tw:flex-col tw:gap-3 tw:border-0 tw:p-0 tw:m-0 tw:min-w-0 tw:mt-2">
            {presets.map((p) => {
              const inputId = `delay-weeks-${p.weeks}`;
              const selected = selectedWeeks === p.weeks;
              return (
                <label
                  key={p.weeks}
                  htmlFor={inputId}
                  className={cn(
                    "tw:flex tw:cursor-pointer tw:items-center tw:gap-3 tw:rounded-md tw:border tw:p-4 tw:text-sm tw:transition-colors",
                    selected
                      ? "tw:border-[#523D33] tw:bg-[#FDEDEE]/50"
                      : "tw:border-gray-200 tw:bg-white hover:tw:bg-gray-50/80",
                  )}
                >
                  <input
                    id={inputId}
                    type="radio"
                    name="delay-weeks"
                    value={p.weeks}
                    checked={selected}
                    disabled={confirmPending}
                    onChange={() => setSelectedWeeks(p.weeks)}
                    className="tw:size-4.5 tw:shrink-0 tw:accent-[#523D33] tw:cursor-pointer"
                  />
                  <div className="tw:flex tw:flex-col tw:flex-1 tw:min-w-0">
                    <span
                      className="tw:font-medium tw:leading-snug"
                      style={{ color: "var(--so-text, #523D33)" }}
                    >
                      {p.weeks} Weeks
                    </span>
                    <span className="tw:text-xs tw:text-[#523D33]/70 tw:leading-snug">
                      Delivers {formatSubscriptionDateForDisplay(p.date)}
                    </span>
                  </div>
                </label>
              );
            })}
          </fieldset>
        ) : (
          <p
            className="tw:text-sm tw:my-3"
            style={{ color: "var(--so-text, #523D33)" }}
          >
            No later delivery dates are available right now.
          </p>
        )}

        <DialogFooter className="tw:flex-col tw:sm:flex-col tw:gap-2 tw:mt-4 tw:pt-2">
          <Button
            type="button"
            variant={actionVariant}
            disabled={confirmPending || selectedPreset == null}
            onClick={() => {
              if (selectedPreset != null)
                onConfirm(selectedPreset.date, selectedPreset.weeks);
            }}
            className={actionPillClass}
          >
            {confirmPending ? (
              <Loader2 className="tw:size-4 tw:animate-spin tw:mx-auto" />
            ) : (
              "Delay"
            )}
          </Button>
          <Button
            type="button"
            variant={dismissVariant}
            disabled={confirmPending}
            onClick={() => onOpenChange(false)}
            className={dismissPillClass}
          >
            Never mind
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
